// Login screen — adapted from inku/frontend/web/app/[locale]/(unprotected)/(auth)/login/page.tsx.
// Same headline + tagline, FreightDisp serif, two-tone hierarchy. The
// carousel column is dropped (we don't have those assets) — replaced
// with a soft Inku-blue gradient panel for visual balance.
//
// Auth model: validates credentials against /briefings (the "real"
// gated probe). On success, base64(user:pass) is persisted to localStorage
// (handled by api.signIn) so subsequent fetches authorize automatically.

import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

export function LoginPage() {
  const { signIn } = useStore();

  const [username, setUsername] = useState("inku");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setError("Password is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await signIn(username, password);
      if (!r.ok) {
        setError(
          r.status === 401
            ? "Wrong username or password."
            : r.status === 0
              ? "Couldn't reach the server."
              : `Sign-in failed (HTTP ${r.status}).`
        );
      }
      // On success, the store flips authenticated=true and App swaps
      // LoginPage out for the main shell — no manual reload.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-background">
      {/* Left: form */}
      <div className="flex-1 flex items-center justify-center px-6 md:px-12 py-12">
        <div className="w-full max-w-[420px] space-y-10">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-foreground flex items-center justify-center">
              <span className="font-freight text-background text-lg font-medium leading-none">
                ı
              </span>
            </div>
            <span className="text-base font-semibold text-foreground">
              Inku
            </span>
          </div>

          {/* Headline */}
          <div className="space-y-3">
            <h1 className="font-freight font-normal leading-[1.15] tracking-tight">
              <span className="block text-4xl md:text-[2.75rem] text-foreground">
                Your brand knows what it wants.
              </span>
              <span className="block text-3xl md:text-[2.25rem] text-muted-foreground/70 mt-1">
                Now your AI does too.
              </span>
            </h1>
            <p className="text-sm text-muted-foreground pt-1">
              The platform for on-brand generation
            </p>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="space-y-4">
            <Field
              label="Username"
              value={username}
              onChange={setUsername}
              autoComplete="username"
              autoFocus
            />
            <Field
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[hsl(var(--inku-blue))] hover:bg-[hsl(var(--inku-blue))]/90"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground/80">
            Demo environment · single-account access · credentials provided
            out of band
          </p>
        </div>
      </div>

      {/* Right: brand panel (replaces carousel) */}
      <div className="hidden lg:flex flex-1 items-center justify-center pl-12 pr-12 py-12">
        <div className="relative w-full max-w-[560px] aspect-square">
          <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-[hsl(var(--inku-blue))]/10 via-[hsl(var(--inku-blue))]/5 to-transparent blur-2xl" />
          <div className="relative w-full h-full rounded-[2rem] bg-card border border-border shadow-card-hover overflow-hidden flex items-center justify-center">
            <div className="text-center space-y-2 px-12">
              <div className="font-freight text-7xl text-[hsl(var(--inku-blue))] font-light leading-none">
                ınku
              </div>
              <p className="font-freight italic text-2xl text-muted-foreground/80 mt-6">
                creative direction, on demand.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  autoComplete,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
  autoFocus?: boolean;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        className={cn(
          "w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
          "outline-none transition-shadow input-focus-glow"
        )}
      />
    </label>
  );
}
